import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

const AUTH_STORAGE_KEY = 'admin_auth';

interface AuthData {
  token: string;
  expiresAt: number;
}

/**
 * Stores auth token in localStorage with 1-day expiration
 */
export function setAuthToken(token: string): void {
  const authData: AuthData = {
    token,
    expiresAt: dayjs().add(1, 'day').valueOf(),
  };
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
}

/**
 * Retrieves auth token if valid, returns null if expired or missing
 */
export function getAuthToken(): string | null {
  const storedData = localStorage.getItem(AUTH_STORAGE_KEY);

  if (!storedData) {
    return null;
  }

  try {
    const authData: AuthData = JSON.parse(storedData);

    // Check if token is expired
    if (dayjs().isAfter(dayjs(authData.expiresAt))) {
      clearAuthToken();
      return null;
    }

    return authData.token;
  } catch {
    // Invalid JSON, clear storage
    clearAuthToken();
    return null;
  }
}

/**
 * Removes auth token from localStorage
 */
export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

/**
 * Checks if user is authenticated with valid token
 */
export function isAuthenticated(): boolean {
  return getAuthToken() !== null;
}

/**
 * React hook for authentication state
 * Returns: { isAuthenticated: boolean, token: string | null, logout: () => void }
 */
export function useAuth() {
  const [token, setToken] = useState<string | null>(getAuthToken());
  const [isAuth, setIsAuth] = useState<boolean>(isAuthenticated());

  // Refresh auth state when component mounts or storage changes
  useEffect(() => {
    const checkAuth = () => {
      const currentToken = getAuthToken();
      setToken(currentToken);
      setIsAuth(currentToken !== null);
    };

    // Check auth state
    checkAuth();

    // Listen for storage changes (in case of logout from another tab)
    window.addEventListener('storage', checkAuth);

    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  const logout = () => {
    clearAuthToken();
    setToken(null);
    setIsAuth(false);
  };

  return {
    isAuthenticated: isAuth,
    token,
    logout,
  };
}
